import { AssetsService, Asset } from '@ts/services/assetsService';
import { Editor, Ui } from 'tinymce';

type Spec = Ui.Dialog.CheckboxSpec & {
  name: string;
};

const processResults = (editor: Editor, assets: Asset[]) => {
  const images = assets
    .map((asset, idx) => {
      const wasAdded = editor
        .getDoc()
        .querySelector(`#placeholder-image-${idx + 1}`);

      return asset['@type'].includes('image') && !wasAdded
        ? {
            type: 'checkbox',
            label: `Image ${idx + 1} | ${asset.title} (${asset.review_state || 'N/A'})`,
            name: `image-${idx + 1}`,
          }
        : null;
    })
    .filter((item): item is Spec => item !== null);

  if (images.length > 0) return images;

  editor.notificationManager.open({
    text: 'No additional images available.',
    type: 'info',
    timeout: 3000,
  });
};

const getImageAssets = async (editor: Editor, service: AssetsService) => {
  try {
    const assets: Asset[] = await service.getAssets();

    const images = assets.filter(
      asset => asset['@type'] === 'freitag.article.image',
    );

    return processResults(editor, images);
  } catch (error) {
    if (error instanceof Error) {
      editor.notificationManager.close();
      editor.notificationManager.open({
        text: 'Failed to load Image Files, please try again and contact developers if the issue persists.',
        type: 'error',
        timeout: 5000,
      });

      console.error(`Error getting Image Assets: ${error.message}`);
    }
  }
};

/**
 * Open a modal dialog to Image Placeholder
 */
const openPlaceholderDialog = async (
  editor: Editor,
  service = new AssetsService(),
) => {
  // Image Placeholders are only available to Edit Forms (not Add Forms)
  if (window.location.href.includes('++add++')) {
    editor.notificationManager.close();
    editor.notificationManager.open({
      text: 'When creating a post, no placeholder can be created. Please save and edit again.',
      type: 'error',
      timeout: 5000,
    });

    return;
  }

  const images = await getImageAssets(editor, service);
  if (!images) return;

  editor.windowManager.open({
    title: 'Additional files',
    body: {
      type: 'panel',
      items: images,
    },
    buttons: [
      { text: 'Insert', type: 'submit', primary: true },
      { text: 'Cancel', type: 'cancel' },
    ],
    onSubmit: api => {
      const data = api.getData();
      const content = Object.keys(data)
        .filter(id => data[id])
        .map(
          id =>
            `<div class="placeholder" id="placeholder-${id}">Placeholder ${id}</div>`,
        )
        .join('');

      const placeholderHtml = `${content}<p></p>`;

      editor.insertContent(placeholderHtml, { format: 'raw' });
      api.close();
    },
  });
};

/**
 * Add a custom Image Placeholder button to the toolbar
 */
export const placeholder = (editor: Editor) => {
  editor.ui.registry.addButton('placeholderButton', {
    icon: 'image',
    tooltip: 'Insert placeholder',
    onAction() {
      openPlaceholderDialog(editor);
    },
  });
};
